import React, { FC, PropsWithChildren, useEffect } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import classNames from 'classnames'
import { useMediaQuery } from 'react-responsive'
import { LazyHydrate } from '@/atoms/LazyHydrate'

interface ModalSheetProps extends PropsWithChildren {
  isOpen: boolean
  onClose: () => void
  panelClasses?: string
  backgroundScreenColor?: 'black' | 'white'
  backgroundOverlayClassName?: string
  maxHeight?: string
  sheetTitle?: string
  sheetHeight?: string
}

export const ModalSheet: FC<ModalSheetProps> = ({
  isOpen,
  onClose,
  children,
  panelClasses,
  backgroundScreenColor = 'black',
  backgroundOverlayClassName,
  maxHeight = '80vh',
  sheetTitle,
  sheetHeight = 'calc(100vh - 28px)',
}) => {
  const isDesktop = useMediaQuery({ minWidth: 768 }) // Matches Tailwind's md breakpoint

  // Prevent body scrolling when modal/sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <>
      {/* Backdrop */}
      <div
        className={classNames(
          'fixed inset-0 z-[100] transition-opacity',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
          backgroundScreenColor === 'black' ? 'bg-black/65' : 'bg-white/75',
          backgroundOverlayClassName,
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <LazyHydrate whenVisible>
        {isDesktop ? (
          // Desktop Modal
          <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-[100] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <DialogPanel
                className={classNames(
                  'relative z-10 transform rounded-2xl bg-white text-left align-middle shadow-xl transition-all overflow-hidden',
                  panelClasses,
                )}
                style={{ maxHeight }}
              >
                {children}
              </DialogPanel>
            </div>
          </Dialog>
        ) : (
          // Mobile Sheet
          <div
            className={classNames(
              'fixed inset-x-0 bottom-0 z-[100] flex flex-col rounded-t-2xl bg-white shadow-xl transition-transform duration-300 ease-in-out',
              isOpen ? 'translate-y-0' : 'translate-y-full',
              panelClasses,
            )}
            style={{ height: sheetHeight }}
          >
            {/* Sheet handle/drag indicator */}
            <div className="relative flex w-full shrink-0 flex-col items-center pb-2 pt-4">
              <div className="h-1.5 w-12 rounded-full bg-core-gray-300" />
              {sheetTitle && <h3 className="mt-2 text-lg font-medium text-core-gray-900">{sheetTitle}</h3>}
            </div>

            {/* Sheet content - will scroll independently */}
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
          </div>
        )}
      </LazyHydrate>
    </>
  )
}
